import { Theme } from '@mui/material/styles'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import { red } from '@mui/material/colors'
import InfoIcon from '@mui/icons-material/Info'

import config from 'src/config'
import formatEuro from 'src/functions/formatEuro'
import { discountPrice } from 'src/shared/frontendTypes/product'

interface Props {
  price: number | discountPrice
  qty?: number
  className?: string
  align?: 'left' | 'right'
  priceFactor?: number
  deliverytime?: number
  stockDeliverytime?: number
  showDeliverytime?: boolean
  showBackorderNotice?: boolean
}

type BreakdownRow = {
  key: string
  qty: number
  regularPrice: number
  discountPrice: number
  label: string
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'grid',
      gap: theme.spacing(.45),
      justifyItems: 'start',
      textAlign: 'left',
      '&.alignRight': {
        justifyItems: 'end',
        textAlign: 'right',
      },
    },

    row: {
      alignItems: 'baseline',
      display: 'grid',
      gap: theme.spacing(.75),
      gridTemplateColumns: 'max-content minmax(0, max-content)',
      justifyContent: 'start',
      lineHeight: 1.35,
      '.alignRight &': {
        justifyContent: 'end',
      },
    },

    qty: {
      fontWeight: 700,
      whiteSpace: 'nowrap',
    },

    prices: {
      alignItems: 'baseline',
      display: 'inline-flex',
      flexWrap: 'wrap',
      gap: theme.spacing(.7),
      minWidth: 0,
      '.alignRight &': {
        justifyContent: 'flex-end',
      },
    },

    strike: {
      color: theme.palette.text.primary,
      fontSize: '.82rem',
      textDecoration: 'line-through',
      whiteSpace: 'nowrap',
    },

    discount: {
      color: red[600],
      fontSize: '.95rem',
      whiteSpace: 'nowrap',
    },

    label: {
      color: theme.palette.text.secondary,
      display: 'grid',
      gap: theme.spacing(.15),
      fontSize: '.76rem',
      gridColumn: '2',
      lineHeight: 1.25,
      marginTop: theme.spacing(-.2),
    },

    notice: {
      alignItems: 'flex-start',
      color: theme.palette.error.main,
      display: 'flex',
      fontSize: '.84rem',
      gap: theme.spacing(.7),
      lineHeight: 1.45,
      marginTop: theme.spacing(.35),
      maxWidth: 380,
      '.alignRight &': {
        justifyContent: 'flex-end',
        textAlign: 'right',
      },
      '& svg': {
        flex: '0 0 auto',
        marginTop: 2,
      },
    },
  })
)

export const getStockDiscountBreakdownRows = (
  price: number | discountPrice,
  qty = 1,
): BreakdownRow[] => {
  if (typeof price === 'number') return []
  if (price.limitStockDiscountToStock !== true) return []
  if (typeof price.stockDiscountLimit !== 'number' || price.stockDiscountLimit <= 0) return []
  if (typeof price.discountPriceAfterStock !== 'number') return []

  const selectedQty = Math.max(1, Math.floor(qty))
  const stockQty = Math.min(selectedQty, Math.max(0, Math.floor(price.stockDiscountLimit)))
  const afterStockQty = Math.max(0, selectedQty - stockQty)

  return [
    ...(stockQty > 0
      ? [{
        key: 'stock',
        qty: stockQty,
        regularPrice: price.regularPrice,
        discountPrice: price.discountPrice,
        label: 'mit Lager-Rabatt',
      }]
      : []),
    ...(afterStockQty > 0
      ? [{
        key: 'after-stock',
        qty: afterStockQty,
        regularPrice: price.regularPrice,
        discountPrice: price.discountPriceAfterStock,
        label: price.discountPriceAfterStock < price.regularPrice
          ? 'mit Bestellware-Rabatt'
          : 'zum regulären Preis',
      }]
      : []),
  ]
}

export const hasStockDiscountPriceBreakdown = (
  price: number | discountPrice,
  qty = 1,
) => getStockDiscountBreakdownRows(price, qty).length > 0

const StockDiscountPriceBreakdown: React.FC<Props> = ({
  price,
  qty = 1,
  className,
  align = 'left',
  priceFactor = 1,
  deliverytime,
  stockDeliverytime,
  showDeliverytime = false,
  showBackorderNotice = false,
}) => {
  const classes = useStyles()
  const rows = getStockDiscountBreakdownRows(price, qty)
  if (!rows.length) return null

  const scalePrice = (value: number) => value * priceFactor
  const resolvedStockDeliverytime = stockDeliverytime ?? config.stockDeliveryTime
  const hasBackorderRow = rows.some(row => row.key === 'after-stock')
  const deliverytimeForRow = (row: BreakdownRow) => {
    if (!showDeliverytime) return undefined
    return row.key === 'stock'
      ? resolvedStockDeliverytime
      : deliverytime
  }

  return (
    <div className={[
      classes.root,
      align === 'right' ? 'alignRight' : '',
      className,
    ].filter(Boolean).join(' ')}>
      {rows.map(row => (
        <div key={row.key} className={classes.row}>
          <span className={classes.qty}>{row.qty} x</span>
          <span className={classes.prices}>
            {row.discountPrice < row.regularPrice && (
              <span className={classes.strike}>{formatEuro(scalePrice(row.regularPrice))} *¹</span>
            )}
            <span className={classes.discount}>{formatEuro(scalePrice(row.discountPrice))} *¹</span>
          </span>
          <span className={classes.label}>
            <span>{row.label}</span>
            {deliverytimeForRow(row) && (
              <span>{row.qty} x Lieferzeit: {deliverytimeForRow(row)} Werktage*²</span>
            )}
          </span>
        </div>
      ))}
      {showBackorderNotice && hasBackorderRow && (
        <div className={classes.notice}>
          <InfoIcon fontSize='small' />
          <span>Ein Teil der Ware muss beim Hersteller nachbestellt werden.</span>
        </div>
      )}
    </div>
  )
}

export default StockDiscountPriceBreakdown
